import React from 'react';
import {
  Wallet,
  ShoppingCart,
  TrendingDown,
  Gift,
  Info,
  Calendar,
  Hash,
  Sparkles,
  Clock,
} from 'lucide-react';
import { SectionCard, InfoRow, MetricCard, TabLoading, TabError, TabEmpty } from './shared';
import { useUserXuBux } from '../../hooks/useUserTabs';
import { formatDateTime, formatNumber } from '../../utils/helpers';

interface XuBuxBalanceData {
  id: number;
  userId?: number;
  balance?: number;
  cachedBalance?: number;
  cachedBalanceAt?: string | null;
  totalPurchased?: number;
  totalEarned?: number;
  totalUsed?: number;
  totalSpent?: number;
  totalGranted?: number;
  totalExpired?: number;
  freeGenerationsUsed?: number;
  freeGenerationsMax?: number;
  createdAt: string;
  updatedAt: string;
}

interface XuBuxTabProps {
  userId: number;
}

const XuBuxTab: React.FC<XuBuxTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserXuBux(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No XuBux account found for this user" icon={Wallet} />;

  const d = data as XuBuxBalanceData;
  const balance = d.cachedBalance ?? d.balance ?? 0;
  const totalSpent = d.totalSpent ?? d.totalUsed ?? 0;
  const totalEarned = d.totalEarned ?? d.totalPurchased ?? 0;
  const totalExpired = d.totalExpired ?? 0;
  const totalGranted = d.totalGranted ?? 0;
  const freeUsed = d.freeGenerationsUsed ?? 0;
  const freeMax = d.freeGenerationsMax ?? 0;
  const freeRemaining = Math.max(0, freeMax - freeUsed);

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          label="Current Balance"
          value={formatNumber(balance)}
          icon={Wallet}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <MetricCard
          label="Total Bought"
          value={formatNumber(totalEarned)}
          icon={ShoppingCart}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Total Spent"
          value={formatNumber(totalSpent)}
          icon={TrendingDown}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
        <MetricCard
          label="Total Granted"
          value={formatNumber(totalGranted)}
          icon={Gift}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
        <MetricCard
          label="Total Expired"
          value={formatNumber(totalExpired)}
          icon={Clock}
          color="text-red-400"
          bgColor="bg-red-500/10"
        />
        <MetricCard
          label="Free Generations"
          value={`${freeUsed} / ${freeMax}`}
          icon={Sparkles}
          color="text-purple-400"
          bgColor="bg-purple-500/10"
          subtitle={`${freeRemaining} remaining`}
        />
      </div>

      {/* Free Generations Progress */}
      {freeMax > 0 && (
        <SectionCard title="Free AI Generations" icon={Sparkles} subtitle={`${freeUsed} of ${freeMax} used`}>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Usage</span>
              <span className="font-medium text-foreground">{freeUsed} / {freeMax}</span>
            </div>
            <div className="h-2.5 rounded-full bg-muted/30 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  freeRemaining === 0
                    ? 'bg-red-500'
                    : freeUsed / freeMax > 0.66
                      ? 'bg-amber-500'
                      : 'bg-purple-500'
                }`}
                style={{ width: `${Math.min(100, (freeUsed / freeMax) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {freeRemaining === 0
                ? 'All free generations have been used'
                : `${freeRemaining} free generation${freeRemaining !== 1 ? 's' : ''} remaining`}
            </p>
          </div>
        </SectionCard>
      )}

      {/* Balance Details */}
      <SectionCard title="Account Details" icon={Info} subtitle="XuBux account information">
        <div className="space-y-1">
          <InfoRow
            label="Account ID"
            icon={Hash}
            value={d.id}
            mono
          />
          <InfoRow
            label="Cached Balance"
            icon={Wallet}
            value={
              <span className="text-emerald-400 font-semibold">
                {formatNumber(balance)}
              </span>
            }
          />
          {d.cachedBalanceAt && (
            <InfoRow
              label="Balance Cached At"
              icon={Clock}
              value={formatDateTime(d.cachedBalanceAt)}
            />
          )}
          <InfoRow
            label="Total Bought"
            icon={ShoppingCart}
            value={formatNumber(totalEarned)}
          />
          <InfoRow
            label="Total Spent"
            icon={TrendingDown}
            value={formatNumber(totalSpent)}
          />
          <InfoRow
            label="Total Granted"
            icon={Gift}
            value={formatNumber(totalGranted)}
          />
          <InfoRow
            label="Total Expired"
            icon={Clock}
            value={formatNumber(totalExpired)}
          />
          <InfoRow
            label="Free Generations Used"
            icon={Sparkles}
            value={`${freeUsed} / ${freeMax}`}
          />
          <InfoRow
            label="Account Created"
            icon={Calendar}
            value={formatDateTime(d.createdAt)}
          />
          <InfoRow
            label="Last Updated"
            icon={Calendar}
            value={formatDateTime(d.updatedAt)}
          />
        </div>
      </SectionCard>
    </div>
  );
};

export default XuBuxTab;
